import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { LockKey, ArrowRight } from "@phosphor-icons/react";
import { LOGO_URL, BRAND } from "@/lib/api";
import { toast } from "sonner";

function formatErr(detail) {
  if (!detail) return "Login failed";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function AdminLogin() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = "Admin · True North Maintenance";
    if (user) navigate("/admin", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      const to = location.state?.from?.pathname || "/admin";
      navigate(to, { replace: true });
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Could not log in");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="admin-login-page" className="min-h-screen flex">
      <div className="hidden md:flex md:w-1/2 bg-navy-deep relative items-center justify-center p-12 overflow-hidden">
        <div className="absolute inset-0 hero-grain opacity-30" />
        <div className="relative text-center max-w-md">
          <img src={LOGO_URL} alt={BRAND.name} className="w-48 mx-auto" />
          <div className="mt-8 text-xs uppercase tracking-[0.32em] text-[#C9A24C] font-semibold">Marketing Operations</div>
          <h1 className="font-display font-black text-white text-4xl mt-3 leading-tight">Admin command centre.</h1>
          <p className="text-slate-400 mt-3 text-sm leading-relaxed">SEO, ads, social, leads and reviews — all in one place.</p>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-6 md:p-12 bg-cream">
        <Card className="w-full max-w-md p-8 border border-slate-200 rounded-sm bg-white shadow-sm">
          <div className="md:hidden mb-6 text-center">
            <img src={LOGO_URL} alt={BRAND.name} className="w-32 mx-auto bg-navy-deep p-3 rounded-sm" />
          </div>
          <div className="flex items-center gap-2 text-navy mb-2"><LockKey size={16} weight="fill" /><span className="eyebrow !text-[10px]">Restricted access</span></div>
          <h2 className="font-display font-black text-3xl text-navy">Sign in</h2>
          <p className="text-sm text-slate-600 mt-1">Use your admin credentials to continue.</p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold text-slate-700">Email</Label>
              <Input data-testid="login-email" type="email" required value={email} onChange={(e)=>setEmail(e.target.value)} className="rounded-sm mt-1.5 h-11 border-slate-300" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold text-slate-700">Password</Label>
              <Input data-testid="login-password" type="password" required value={password} onChange={(e)=>setPassword(e.target.value)} className="rounded-sm mt-1.5 h-11 border-slate-300" />
            </div>
            <Button data-testid="login-submit" type="submit" disabled={loading} className="w-full bg-navy hover:bg-navy-deep text-white font-semibold rounded-sm h-12 text-base">{loading ? "Signing in…" : <>Sign in <ArrowRight className="ml-2" size={18} /></>}</Button>
          </form>
          <a href="/" className="text-xs text-slate-500 hover:text-navy mt-6 inline-block">← Back to website</a>
        </Card>
      </div>
    </div>
  );
}
